import React, { useState, useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import {
    Box,
    Card,
    Typography,
    Grid,
    Avatar,
    Button,
    styled,
    useTheme,
    useMediaQuery,
    Fab,
    Zoom,
    Tooltip,
    Stack,
} from "@mui/material";

import { AiOutlineRead } from "react-icons/ai";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import CodeIcon from "@mui/icons-material/Code";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import CheckIcon from "@mui/icons-material/Check";


/* ✅ Assets */
const bgImage = "https://images.unsplash.com/photo-1499951360447-b19be8fe80f5";

/* ✅ Styled Components */
const PageWrapper = styled(Box)(({ theme }) => ({
    minHeight: "100vh",
    backgroundImage: `linear-gradient(rgba(16,7,32,0.9), rgba(16,7,32,0.95)), url(${bgImage})`,
    backgroundSize: "cover",
    backgroundPosition: "center",
    paddingBottom: "80px",
    paddingTop: theme.spacing(12),
    color: "white",
}));

const SectionCard = styled(Card)(({ theme }) => ({
    backgroundColor: "rgba(16,7,32,0.85)",
    borderRadius: "14px",
    padding: theme.spacing(3),
    marginBottom: "25px",
    boxShadow: "0 4px 14px rgba(0,0,0,0.25)",
    border: "1px solid rgba(255,255,255,0.1)",
    color: "white",
}));

const CodeBlock = styled("pre")(() => ({
    backgroundColor: "#0d1117",
    color: "#c9d1d9",
    padding: "20px",
    borderRadius: "10px",
    overflowX: "auto",
    fontSize: "0.9rem",
    lineHeight: 1.6,
    fontFamily: "'Fira Code', monospace",
}));

const ActionButton = styled(Button)(() => ({
    backgroundColor: "goldenrod",
    color: "#100720",
    fontWeight: 800,
    padding: "12px 40px",
    borderRadius: "999px",
    "&:hover": { backgroundColor: "#e0b200", transform: "scale(1.05)" },
}));

export default function PythonOperators() {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
    const [showScroll, setShowScroll] = useState(false);
    const [copiedIndex, setCopiedIndex] = useState(null);

    useEffect(() => {
        const checkScroll = () => setShowScroll(window.pageYOffset > 400);
        window.addEventListener("scroll", checkScroll);
        return () => window.removeEventListener("scroll", checkScroll);
    }, []);

    const handleCopy = (text, index) => {
        navigator.clipboard.writeText(text);
        setCopiedIndex(index);
        setTimeout(() => setCopiedIndex(null), 2000);
    };

    const operatorModules = useMemo(() => [
        {
            title: "1. Arithmetic Operators",
            code: `x = 10\ny = 3\n\nprint(x + y)   # Addition: 13\nprint(x / y)   # Float Division: 3.333...\nprint(x // y)  # Floor Division: 3\nprint(x % y)   # Modulus: 1\nprint(x ** y)  # Exponentiation: 1000`,
            desc: "Perform mathematical operations. Python includes the floor division (//) which discards the remainder, and exponentiation (**) for powers.",
            diagram: ""
        },
        {
            title: "2. Comparison Operators",
            code: `x = 10\ny = 3\n\nprint(x > y)    # True\nprint(x == y)   # False\nprint(x != y)   # True\nprint(x <= 10)  # True`,
            desc: "Compare two values to return a Boolean (True or False). These are the foundation of decision-making in your code.",
            diagram: ""
        },
        {
            title: "3. Logical Operators",
            code: `x = 5\n\n# 'and' returns True if both are True\nprint(x > 3 and x < 10)  # True\n\n# 'not' reverses the result\nprint(not(x == 5))       # False`,
            desc: "Combine multiple conditional statements using English keywords 'and', 'or', and 'not'.",
            diagram: "[Image of truth tables for AND, OR, and NOT logical operators]"
}
], []);

    return (
        <PageWrapper>
            <Box sx={{ textAlign: "center", mb: 6 }}>
                <Typography variant={isMobile ? "h4" : "h3"} sx={{ fontWeight: 800, color: "goldenrod" }}>
                    PYTHON OPERATORS
                </Typography>
            </Box>

            <Grid container spacing={4} sx={{ px: { xs: 2, md: 6 } }}>
                {operatorModules.map((item, i) => (
                    <Grid item xs={12} key={i}>
                        <SectionCard>
                            <Stack direction="row" justifyContent="space-between" alignItems="center" mb={3}>
                                <Stack direction="row" spacing={2} alignItems="center">
                                    <Avatar sx={{ bgcolor: "rgba(218, 165, 32, 0.2)", color: "goldenrod" }}>
                                        <AiOutlineRead size={24} />
                                    </Avatar>
                                    <Typography variant="h6" fontWeight={700}>{item.title}</Typography>
                                </Stack>
                                <Stack direction="row" spacing={1}>
                                    <Tooltip title="Copy Code">
                                        <Button size="small" variant="outlined" onClick={() => handleCopy(item.code, i)} sx={{ color: "white" }}>
                                            {copiedIndex === i ? <CheckIcon fontSize="small" /> : <ContentCopyIcon fontSize="small" />}
                                        </Button>
                                    </Tooltip>
                                    <Link to="/compiler" style={{ textDecoration: "none" }}>
                                        <Button size="small" variant="outlined" startIcon={<CodeIcon />} sx={{ color: "goldenrod", borderColor: "goldenrod" }}>Try it</Button>
                                    </Link>
                                </Stack>
                            </Stack>
                            <Typography variant="body1" sx={{ mb: 2, opacity: 0.9 }}>{item.desc}</Typography>

                            <Box sx={{ textAlign: 'center', my: 2 }}>
                                {item.diagram}
                            </Box>

                            <CodeBlock>{item.code}</CodeBlock>
                        </SectionCard>
                    </Grid>
                ))}
                <Grid item xs={12} textAlign="center">
                    <Link to="/pythonCodingDashBoard" style={{ textDecoration: "none" }}>
                        <ActionButton variant="contained">BACK TO DASHBOARD</ActionButton>
                    </Link>
                </Grid>
            </Grid>

            <Zoom in={showScroll}>
                <Fab size="small" onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })} sx={{ position: "fixed", bottom: 25, right: 25, bgcolor: "goldenrod" }}>
                    <KeyboardArrowUpIcon />
                </Fab>
            </Zoom>
        </PageWrapper>
    );
}
